import React, { useContext, useState, useRef, useEffect } from "react";
import "./layout.css";
import { AppContext } from "../../App";
import { useNavigate, useLocation } from "react-router-dom";
import About from '../about/About';
import UserTripsTutorialModal from '../usertrips/UserTripsTutorialModal';
import TripPlanTreeTutorialModal from '../tripPlanTree/TripPlanTreeTutorialModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = ({ trip  }) => {
  const { userCtx, setUserCtx } = useContext(AppContext);
  const userLayer = userCtx?.layer || 0;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [activeHelpModal, setActiveHelpModal] = useState(null);
  const [tripPlanHelpDontShow, setTripPlanHelpDontShow] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('hideTripPlanTreeTutorial') === 'true';
  });
  const dropdownRef = useRef(null);
  const helpMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isUserTripsPage = location.pathname.startsWith('/userTrips');
  const isTripPlannerPage = location.pathname.startsWith('/trip-details');
  const canShowContextHelp = isUserTripsPage || isTripPlannerPage;
  // Close dropdown on outside click
  
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    function handleHelpMenuOutside(event) {
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target)) {
        setHelpMenuOpen(false);
      }
    }
    if (helpMenuOpen) {
      document.addEventListener("mousedown", handleHelpMenuOutside);
    } else {
      document.removeEventListener("mousedown", handleHelpMenuOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleHelpMenuOutside);
    };
  }, [helpMenuOpen]);

  useEffect(() => {
    setHelpMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await fetch(`${BACKEND_URL}/logout`, { method: "POST", credentials: "include" });
    setUserCtx(null);
    navigate("/");
  };

  const closeHelpMenu = () => setHelpMenuOpen(false);

  const handleContextHelp = () => {
    if (!canShowContextHelp) return;
    closeHelpMenu();
    if (isUserTripsPage) {
      setActiveHelpModal('userTrips');
      return;
    }
    if (isTripPlannerPage) {
      if (typeof window !== 'undefined') {
        setTripPlanHelpDontShow(localStorage.getItem('hideTripPlanTreeTutorial') === 'true');
      }
      setActiveHelpModal('tripPlanTree');
    }
  };

  const handleHelpAbout = () => {
    closeHelpMenu();
    setAboutOpen(true);
  };

  const handleTripPlanHelpClose = () => {
    if (typeof window !== 'undefined') {
      if (tripPlanHelpDontShow) {
        localStorage.setItem('hideTripPlanTreeTutorial', 'true');
      } else {
        localStorage.removeItem('hideTripPlanTreeTutorial');
      }
    }
    setActiveHelpModal(null);
  };

  const handleUserTripsHelpClose = () => {
    setActiveHelpModal(null);
  };

  const handleNav = (path) => {
    setDropdownOpen(false);
    closeHelpMenu();
    if (path === "/about") {
      setAboutOpen(true);
    } else 
    if (path === '/trip-ai') {
      const hasCities = Array.isArray(trip.cities) && trip.cities.length > 0;
                  const cityNames = hasCities
                    ? trip.cities.map(city =>
                        city.name
                      )
                    : trip.countries;
                  navigate('/trip-ai', {
                    state: {
                      tripId: trip._id,
                      tripName: trip.tripName,
                      cities: cityNames
                    }})
                          }
    
     else {
      navigate(path);
    }
  };

  return (
    <>
      <nav className="navbar">
        <ul className="menu">
          <li>
            <button className="nav-link-btn" onClick={() => handleNav("/userTrips")}>Home</button>
          </li>
            {trip && userLayer >= 2 &&
            <li>
              <button className="nav-link-btn" onClick={() => handleNav("/trip-ai")}>AI-Insight</button>
            </li>
          }
          <li className="help-menu" ref={helpMenuRef}>
            <button
              className="nav-link-btn"
              aria-haspopup="true"
              aria-expanded={helpMenuOpen}
              onClick={() => {
                setHelpMenuOpen(open => !open);
                setDropdownOpen(false);
              }}
            >
              Help
            </button>
            {helpMenuOpen && (
              <div className="help-dropdown" role="menu">
                <button
                  className="dropdown-item"
                  role="menuitem"
                  onClick={handleContextHelp}
                  disabled={!canShowContextHelp}
                  title={!canShowContextHelp ? "Open Trips or Planner to access contextual help" : undefined}
                >
                  Help
                </button>
                <button
                  className="dropdown-item"
                  role="menuitem"
                  onClick={handleHelpAbout}
                >
                  About
                </button>
              </div>
            )}
          </li>
        </ul>
        <div className="profile" ref={dropdownRef}>
          <button
            className="profile-name-btn"
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
            onClick={() => setDropdownOpen((open) => !open)}
          >
            {userCtx?.name || userCtx?.username || "User"}
          </button>
          {dropdownOpen && (
            <div className="profile-dropdown" role="menu">
              <button className="dropdown-item" onClick={() => handleNav("/register?mode=edit")} role="menuitem" style={{ whiteSpace: 'nowrap' }}>Update Profile</button>
              <button className="dropdown-item" onClick={handleSignOut} role="menuitem">Log Out</button>
            </div>
          )}
        </div>
      </nav>
      {aboutOpen && (
        <About userName={userCtx?.name || userCtx?.username || "User"} onClose={() => setAboutOpen(false)} />
      )}
      {activeHelpModal === 'userTrips' && (
        <UserTripsTutorialModal onClose={handleUserTripsHelpClose} />
      )}
      {activeHelpModal === 'tripPlanTree' && (
        <TripPlanTreeTutorialModal
          onClose={handleTripPlanHelpClose}
          dontShowChecked={tripPlanHelpDontShow}
          setDontShowChecked={setTripPlanHelpDontShow}
        />
      )}
    </>
  );
};

export default Navbar;
